import { useMultipleFilesUpload } from '@nhost/react';
import { useMemo } from 'react';

import { FileInputProps } from './file-input';
import {
  FileUploadResult,
  getFileUploadResult,
  getMultipleFilesUploadResult,
  MultipleFilesUploadResult,
  UploadedFile,
} from './shared';

export function useEagerFileUpload({
  file,
  onFileUpload,
  onFileReupload,
  onFileDelete,
  onFileDownload,
}: {
  file?: UploadedFile | null;
  onFileUpload?: (result: FileUploadResult) => Promise<void>;
  onFileReupload?: (result: FileUploadResult, existingFileId: string) => Promise<void>;
  onFileDelete?: () => Promise<void>;
  onFileDownload?: (fileId: string) => void;
}) {
  const fileUpload = useEagerMultipleFilesUpload({
    files: file ? [file] : undefined,
    onFilesUpload: onFileUpload
      ? async (uploadResult) => {
          await onFileUpload(getFileUploadResult(uploadResult));
        }
      : undefined,
    onFileReupload: onFileReupload
      ? async (uploadResult, existingFileId) => {
          await onFileReupload(getFileUploadResult(uploadResult), existingFileId);
        }
      : undefined,
    onFileDelete,
    onFileDownload,
  });

  return {
    ...fileUpload,
    isMulti: false,
  };
}

export function useEagerMultipleFilesUpload({
  files,
  onFilesUpload,
  onFileReupload,
  onFileDelete,
  onFileDownload,
}: {
  files?: UploadedFile[];
  onFilesUpload?: (result: MultipleFilesUploadResult) => Promise<void>;
  onFileReupload?: (result: MultipleFilesUploadResult, existingFileId: string) => Promise<void>;
  onFileDelete?: (fileId: string) => Promise<void>;
  onFileDownload?: (fileId: string) => void;
}): FileInputProps {
  const filesUpload = useMultipleFilesUpload();
  const stagedFiles = useMemo(
    () =>
      files
        ? filesUpload.files.filter((stagedFile) => !files.some((file) => file.id === stagedFile.id))
        : filesUpload.files,
    [filesUpload.files, files],
  );

  return {
    isMulti: true,
    uploadedFiles: files,
    onUploadedFileDownload: onFileDownload,
    onUploadedFileDelete: onFileDelete ? (file) => onFileDelete(file.id) : undefined,
    stagedFiles,
    onStagedFileDelete: (fileId) => {
      filesUpload.files.find((file) => file.id === fileId)?.send('DESTROY');
    },
    onStagedFilesAdd: onFilesUpload
      ? async (files) => {
          const uploadResult = await filesUpload.upload({ files });
          await onFilesUpload(getMultipleFilesUploadResult(uploadResult));
          filesUpload.clear();
        }
      : undefined,
    onUploadedFileReupload: onFileReupload
      ? async (file, files) => {
          const uploadResult = await filesUpload.upload({ files });
          await onFileReupload(getMultipleFilesUploadResult(uploadResult), file.id);
          filesUpload.clear();
        }
      : undefined,
  };
}
